const CLOSE_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;

// ── Modal Principal ─────────────────────────────────────────────────────────

export class AppModal extends HTMLElement {
  static get observedAttributes() {
    return ["title", "open", "size"];
  }

  connectedCallback() {
    this.render();
    document.addEventListener("keydown", this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeydown);
  }

  private handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.hasAttribute("open")) {
      this.close();
    }
  };

  attributeChangedCallback() { this.render(); }

  open()  { this.setAttribute("open", ""); }
  close() {
    this.removeAttribute("open");
    this.dispatchEvent(new CustomEvent("close", { bubbles: true }));
  }

  private render() {
    const isOpen = this.hasAttribute("open");
    const title = this.getAttribute("title") || "Janela";
    const size = this.getAttribute("size") || "md"; // sm | md | lg | xl
    const bodyContent  = this.querySelector("[slot='body']")?.innerHTML  || "";
    const footerContent = this.querySelector("[slot='footer']")?.innerHTML || "";

    if (!isOpen) {
      this.style.display = "none";
      return;
    }

    this.style.display = "block";
    this.innerHTML = `
      <div class="modal-overlay" role="presentation">
        <div class="modal-content modal-${size}" role="dialog" aria-modal="true" aria-labelledby="modal-title-${size}">
          <div class="modal-header">
            <h3 id="modal-title-${size}" class="text-subsection-title">${title}</h3>
            <button type="button" class="btn-icon close-modal-btn" aria-label="Fechar janela" title="Fechar">
              ${CLOSE_ICON}
            </button>
          </div>
          <div class="modal-body">${bodyContent}</div>
          ${footerContent ? `<div class="modal-footer">${footerContent}</div>` : ""}
        </div>
      </div>
    `;

    this.querySelector(".close-modal-btn")?.addEventListener("click", () => this.close());
    this.querySelector(".modal-overlay")?.addEventListener("click", (e) => {
      if (e.target === e.currentTarget) this.close();
    });
  }
}

if (!customElements.get("app-modal")) {
  customElements.define("app-modal", AppModal);
}

// ── Diálogo de Confirmação ──────────────────────────────────────────────────

export class AppConfirmDialog extends HTMLElement {
  static get observedAttributes() {
    return ["title", "message", "confirm-label", "cancel-label", "danger", "open"];
  }

  connectedCallback() {
    this.render();
    document.addEventListener("keydown", this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeydown);
  }

  private handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.hasAttribute("open")) {
      this.close();
      this.dispatchEvent(new CustomEvent("cancel"));
    }
  };

  attributeChangedCallback() { this.render(); }

  open()  { this.setAttribute("open", ""); }
  close() { this.removeAttribute("open"); }

  private render() {
    const isOpen = this.hasAttribute("open");
    const title = this.getAttribute("title") || "Confirmar Ação";
    const message = this.getAttribute("message") || "Tem certeza de que deseja prosseguir?";
    const confirmLabel = this.getAttribute("confirm-label") || "Confirmar";
    const cancelLabel  = this.getAttribute("cancel-label")  || "Cancelar";
    const isDanger = this.hasAttribute("danger");

    if (!isOpen) {
      this.style.display = "none";
      return;
    }

    this.style.display = "block";
    this.innerHTML = `
      <div class="modal-overlay" role="presentation">
        <div class="modal-content modal-sm" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-msg">
          <div class="modal-header">
            <h3 id="confirm-title" class="text-subsection-title" style="${isDanger ? "color: var(--status-danger-text);" : ""}">${title}</h3>
          </div>
          <div class="modal-body">
            <p id="confirm-msg" class="text-body" style="margin: 0;">${message}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary btn-cancel">${cancelLabel}</button>
            <button type="button" class="btn ${isDanger ? "btn-danger" : "btn-primary"} btn-confirm">${confirmLabel}</button>
          </div>
        </div>
      </div>
    `;

    this.querySelector(".btn-cancel")?.addEventListener("click", () => {
      this.close();
      this.dispatchEvent(new CustomEvent("cancel", { bubbles: true }));
    });

    this.querySelector(".btn-confirm")?.addEventListener("click", () => {
      this.close();
      this.dispatchEvent(new CustomEvent("confirm", { bubbles: true }));
    });

    this.querySelector(".modal-overlay")?.addEventListener("click", (e) => {
      if (e.target === e.currentTarget) {
        this.close();
        this.dispatchEvent(new CustomEvent("cancel", { bubbles: true }));
      }
    });
  }
}

if (!customElements.get("app-confirm-dialog")) {
  customElements.define("app-confirm-dialog", AppConfirmDialog);
}

// ── Drawer Lateral ──────────────────────────────────────────────────────────

export class AppDrawer extends HTMLElement {
  static get observedAttributes() {
    return ["title", "open"];
  }

  connectedCallback() {
    this.render();
    document.addEventListener("keydown", this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeydown);
  }

  private handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.hasAttribute("open")) {
      this.close();
    }
  };

  attributeChangedCallback() { this.render(); }

  open()  { this.setAttribute("open", ""); }
  close() {
    this.removeAttribute("open");
    this.dispatchEvent(new CustomEvent("close", { bubbles: true }));
  }

  private render() {
    const isOpen = this.hasAttribute("open");
    const title = this.getAttribute("title") || "Detalhes Técnicos";
    const bodyContent  = this.querySelector("[slot='body']")?.innerHTML  || "";
    const footerContent = this.querySelector("[slot='footer']")?.innerHTML || "";

    if (!isOpen) {
      this.style.display = "none";
      return;
    }

    this.style.display = "block";
    this.innerHTML = `
      <div class="drawer-overlay" role="presentation">
        <div class="drawer-content" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
          <div class="modal-header">
            <h3 id="drawer-title" class="text-subsection-title">${title}</h3>
            <button type="button" class="btn-icon close-drawer-btn" aria-label="Fechar painel" title="Fechar">
              ${CLOSE_ICON}
            </button>
          </div>
          <div class="modal-body" style="flex: 1;">${bodyContent}</div>
          ${footerContent ? `<div class="modal-footer">${footerContent}</div>` : ""}
        </div>
      </div>
    `;

    this.querySelector(".close-drawer-btn")?.addEventListener("click", () => this.close());
    this.querySelector(".drawer-overlay")?.addEventListener("click", (e) => {
      if (e.target === e.currentTarget) this.close();
    });
  }
}

if (!customElements.get("app-drawer")) {
  customElements.define("app-drawer", AppDrawer);
}
